import { z } from 'zod';
import { api } from './client';
import { itemSchema } from './schemas';
import { setActiveCurrency } from '@/lib/money';

export const mealPeriodTimesSchema = z.object({
  breakfast: z.string(),
  lunch: z.string(),
  dinner: z.string(),
});

/** `GET /settings` payload (typed by the backend: numbers, booleans, object). */
export const settingsSchema = z.looseObject({
  tax_percent: z.number(),
  takeaway_max_orders: z.number(),
  uber_max_orders: z.number(),
  pickme_max_orders: z.number(),
  shifts_enabled: z.boolean(),
  rooms_enabled: z.boolean(),
  print_preview_enabled: z.boolean(),
  receipt_print_enabled: z.boolean(),
  meal_period_times: mealPeriodTimesSchema,
  currency: z.string(),
  business_name: z.string().nullish(),
  business_phone: z.string().nullish(),
  timezone: z.string().optional(),
  low_stock_threshold: z.number().nullish(),
  /** Cap (percent of the subtotal) for a cashier's manual discount; admins are uncapped. 0..100, server default 20. */
  cashier_max_discount_percent: z.number().default(20),
  system_locked: z.boolean(),
  system_lock_message: z.string().nullish(),
  /** Receipt copies per print on the till agent (1..3). */
  receipt_copies: z.number().default(1),
  /** Kitchen tickets: print on the sending till (`browser`) or queue them for the kitchen PC's print agent (`server`). */
  kitchen_print_mode: z.enum(['browser', 'server']).catch('browser').default('browser'),
  /** Queued (server-printed) kitchen tickets carry the customer phone number. Turn off to keep it off the kitchen slip. */
  kot_show_customer_phone: z.boolean().catch(true).default(true),
});
export type Settings = z.infer<typeof settingsSchema>;

/** Used until the first response arrives and when the request fails: safe, feature-off defaults. */
export const DEFAULT_SETTINGS: Settings = {
  tax_percent: 0,
  takeaway_max_orders: 10,
  uber_max_orders: 10,
  pickme_max_orders: 10,
  shifts_enabled: false,
  rooms_enabled: false,
  print_preview_enabled: true,
  receipt_print_enabled: true,
  meal_period_times: { breakfast: '06:00', lunch: '11:00', dinner: '18:00' },
  currency: 'LKR',
  business_name: '',
  business_phone: '',
  timezone: 'Asia/Colombo',
  low_stock_threshold: 0,
  cashier_max_discount_percent: 20,
  system_locked: false,
  system_lock_message: '',
  receipt_copies: 1,
  kitchen_print_mode: 'browser',
  kot_show_customer_phone: true,
};

export type SettingsUpdateInput = Partial<
  Pick<
    Settings,
    | 'tax_percent'
    | 'takeaway_max_orders'
    | 'uber_max_orders'
    | 'pickme_max_orders'
    | 'print_preview_enabled'
    | 'receipt_print_enabled'
    | 'meal_period_times'
    | 'business_name'
    | 'business_phone'
    | 'low_stock_threshold'
    | 'cashier_max_discount_percent'
    | 'receipt_copies'
    | 'kitchen_print_mode'
    | 'kot_show_customer_phone'
    // super_admin only (admin gets 403 SETTING_FORBIDDEN for the whole request):
    | 'rooms_enabled'
    | 'shifts_enabled'
    | 'system_locked'
    | 'system_lock_message'
  >
>;

export async function getSettings(): Promise<Settings> {
  const { data } = await api.get('/settings');
  const settings = itemSchema(settingsSchema).parse(data).data;
  setActiveCurrency(settings.currency);
  return settings;
}

export async function updateSettings(input: SettingsUpdateInput): Promise<Settings> {
  const { data } = await api.patch('/settings', input);
  const settings = itemSchema(settingsSchema).parse(data).data;
  setActiveCurrency(settings.currency);
  return settings;
}
